import { useMutation } from '@apollo/client/react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { usePolling } from '@optools/hooks/usePolling';
import { UPDATE_BROADCAST } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_CONTENT } from '@optools/services/operator-gateway/btc/queries';
import { BtcScheduleContent } from '@optools/services/operator-gateway/btc/types';
import { MediaState } from '@optools/services/operator-gateway/types/generated/graphql';
import { useSearch } from '@tanstack/react-router';
import { Archive, Circle, CircleDot } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { toast } from 'sonner';

const OPTIONS = Object.values(MediaState);
OPTIONS.sort((a, b) => b.localeCompare(a));

const ICON_MAP: Record<MediaState, ReactNode> = {
  [MediaState.ARCHIVE]: <Archive className="size-4" />,
  [MediaState.OFF]: <Circle className="size-4" />,
  [MediaState.ON]: <CircleDot className="size-4" />,
};

export interface MediaStateSelectProps {
  content: BtcScheduleContent;
  gamePk: number;
}

export function MediaStateSelect({ content, gamePk }: MediaStateSelectProps) {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });
  const { mediaId } = content;
  const [mediaState, setMediaState] = useState(content.mediaState.state);

  const [updateBroadcast, { loading }] = useMutation(UPDATE_BROADCAST, {
    onCompleted: ({ updateBroadcast }) => {
      const { reason, success } = updateBroadcast;
      if (!success) {
        toast.error('Failed to update media state', { description: reason, duration: Infinity });
        setMediaState(content.mediaState.state);
      } else {
        startPolling();
      }
    },
    onError: () => setMediaState(content.mediaState.state),
  });

  const { isPolling, startPolling } = usePolling({
    interval: 2000,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to synchronize media state', {
        description: 'Please refresh the media manually via the "Refresh Media" button.',
        duration: Infinity,
      });
    },
    query: BTC_GAME_CONTENT,
    shouldStopPolling: ({ game }) => {
      if (!game?.content) return;
      const content = game?.content?.find((content) => content.mediaId === mediaId);
      return content?.mediaState.state === mediaState;
    },
    variables: { gamePk, scheduleDate: date },
  });

  const handleStateChange = (state: MediaState) => {
    setMediaState(state);
    updateBroadcast({ variables: { mediaId, mediaState: state } });
  };

  const isLoading = !!isPolling || loading;

  return (
    <Select disabled={isLoading} value={mediaState} onValueChange={handleStateChange}>
      <SelectTrigger aria-label="media state" className="w-36 capitalize" loading={isLoading}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {OPTIONS.map((state) => (
          <SelectItem key={state} className="capitalize" value={state}>
            <div className="flex items-center gap-2">
              {ICON_MAP[state]}
              {state.toLocaleLowerCase()}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
